import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { authHeader } from '../../utils/authHeader';

const PAGE_SIZE = 25;
const EMPTY_FORM = { companyName: '', companyEmail: '', companyPhone: '', companyAddress: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const mapError = (t, err) => {
  if (err.response?.status === 401) return t('ga.error.unauthorized');
  if (err.response?.status === 403) return t('ga.error.forbidden');
  if (err.response?.status >= 500) return err.response?.data?.message || err.response?.data?.error || t('ga.error.server');
  if (!err.response) return t('ga.error.network');
  return t('ga.error.generic');
};

// Same accumulating "load more" fetch pattern as the admin-side pages.
// Status is filtered client-side over the loaded window — not part of the
// `/ga/companies` pagination contract, same tradeoff as Coupons' chips.
const useGACompaniesPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchCompanies = useCallback((reset = true) => {
    if (!config?.backendUrl) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { page, pageSize: PAGE_SIZE };
    if (search) params.search = search;
    axios.get(`${config.backendUrl}/ga/companies`, { params, headers: authHeader() })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching companies', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /ga/companies · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, search, t]);

  useEffect(() => { fetchCompanies(true); }, [fetchCompanies]);

  const loadMore = () => fetchCompanies(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const formErrors = useMemo(() => {
    const errs = {};
    if (!form.companyName.trim()) errs.companyName = t('ga.validation.required');
    if (!EMAIL_RE.test(form.companyEmail)) errs.companyEmail = t('ga.validation.email');
    return errs;
  }, [form.companyName, form.companyEmail, t]);
  const isFormValid = Object.keys(formErrors).length === 0;

  const openCreateDrawer = () => {
    setForm(EMPTY_FORM);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  const handleSave = async () => {
    if (!isFormValid || saving) return;
    setSaving(true);
    try {
      await axios.post(`${config.backendUrl}/ga/companies`, form, { headers: authHeader() });
      notify(t('ga.companies.create'));
      setDrawerOpen(false);
      fetchCompanies(true);
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const goToDetail = (company) => navigate(`/ga/companies/${company._id}`);

  const filteredRows = rows.filter((c) => statusFilter === 'all' || (c.status || 'active') === statusFilter);

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? filteredRows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search || statusFilter !== 'all');

  return {
    t, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch, statusFilter, setStatusFilter,
    loadingMore, loadMore, remaining, nextBatchSize, fetchCompanies, goToDetail,
    drawerOpen, form, setForm, formErrors, isFormValid, saving, closeDrawer, openCreateDrawer, handleSave,
    snackbar, setSnackbar,
  };
};

export default useGACompaniesPage;
